"use client";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import clsx from "clsx";
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  getYear,
  isAfter,
  isSameDay,
  isSameMonth,
  setMonth,
  setYear,
  startOfMonth,
  startOfWeek,
  subMonths,
  subYears,
} from "date-fns";
import { es, enUS } from "date-fns/locale";
import { useState } from "react";
import {
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";
import { LuCalendar } from "react-icons/lu";
import { useTranslations, useLocale } from "next-intl";

const maxDate = subYears(new Date(), 18);
const years = Array.from({ length: 100 }, (_, i) => getYear(maxDate) - i);

type View = "year" | "month" | "day";

type Props<T extends FieldValues> = UseControllerProps<T> & {
  label?: string;
  required?: boolean;
  noBorder?: boolean;
  noShadow?: boolean;
  className?: string;
};

export function DateOfBirthPicker<T extends FieldValues>({
  control,
  name,
  rules,
  label,
  required = true,
  noBorder = false,
  noShadow = false,
  className,
}: Props<T>) {
  const t = useTranslations('dateOfBirthPicker');
  const locale = useLocale();
  const dateLocale = locale === 'es' ? es : enUS;
  const weekDays = t.raw('weekDays') as string[];
  const months = Array.from({ length: 12 }, (_, i) =>
    format(new Date(2000, i, 1), "MMMM", { locale: dateLocale })
  );
  
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control, rules });

  const [view, setView] = useState<View>("day");
  const [tempDate, setTempDate] = useState<Date>(
    value ? new Date(value) : maxDate
  );
  const [open, setOpen] = useState(false);

  const selectYear = (year: number) => {
    setTempDate(setYear(tempDate, year));
    setView("month");
  };

  const selectMonth = (month: number) => {
    setTempDate(setMonth(tempDate, month));
    setView("day");
  };

  const selectDay = (day: number) => {
    const finalDate = new Date(
      tempDate.getFullYear(),
      tempDate.getMonth(),
      day
    );
    if (!isAfter(finalDate, maxDate)) {
      // Guardar en formato YYYY-MM-DD para la BD
      onChange(format(finalDate, "yyyy-MM-dd"));
      setOpen(false);
      setView("day");
    }
  };

  const generateDays = () => {
    const start = startOfWeek(startOfMonth(tempDate));
    const end = endOfMonth(tempDate);
    const days: Date[] = [];
    let day = start;
    while (day <= end) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  };

  return (
    <div className="space-y-1">
      {label && (
        <label className="block mb-2 text-sm text-gray-600 font-semibold">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <Popover
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (isOpen && value) {
            setTempDate(new Date(value));
            setView("day");
          }
        }}
      >
        <PopoverTrigger asChild>
          <div
            onClick={() => setOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setOpen(true);
            }}
            className={clsx(
              "block w-full px-4 py-2 mt-2 rounded-md",
              "flex items-center justify-between",
              !noShadow && "shadow-sm",
              !noBorder && "border",
              "focus:outline-none focus:ring focus:ring-brand-400 focus:border-brand-400 focus:ring-opacity-40",
              "hover:border-brand-400 cursor-pointer",
              error && !noBorder ? "border-red-500" : "",
              !error && !noBorder ? "border-gray-200" : "",
              value ? "text-gray-700" : "text-gray-400",
              !className?.includes('bg-') ? "bg-white" : "",
              className
            )}
          >
            {value
              ? format(new Date(value), "dd/MM/yyyy")
              : t('placeholder')}
            <LuCalendar className="ml-2 h-5 w-5" />
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="w-[300px] p-4 bg-white z-[500]"
          align="start"
        >
          <div className="flex justify-between mb-2">
            <button
              onClick={() => setView("year")}
              className="cursor-pointer text-sm font-semibold hover:underline"
            >
              {format(tempDate, "MMMM yyyy", { locale: dateLocale })}
            </button>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setTempDate(subMonths(tempDate, 1))}
                className="text-gray-500 hover:text-gray-800"
              >
                &lt;
              </button>
              <button
                onClick={() => setTempDate(addMonths(tempDate, 1))}
                className="text-gray-500 hover:text-gray-800"
              >
                &gt;
              </button>
            </div>
          </div>

          {view === "year" && (
            <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto">
              {years.map((year) => (
                <button
                  key={year}
                  onClick={() => selectYear(year)}
                  className="text-sm p-2 rounded hover:bg-gray-100"
                >
                  {year}
                </button>
              ))}
            </div>
          )}

          {view === "month" && (
            <div className="grid grid-cols-3 gap-2">
              {months.map((month, i) => (
                <button
                  key={month}
                  onClick={() => selectMonth(i)}
                  className="text-sm p-2 rounded hover:bg-gray-100"
                >
                  {month}
                </button>
              ))}
            </div>
          )}

          {view === "day" && (
            <>
              <div className="grid grid-cols-7 text-xs text-center mb-1 font-medium text-muted-foreground">
                {weekDays.map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 text-center">
                {generateDays().map((date, idx) => {
                  const disabled =
                    !isSameMonth(date, tempDate) || isAfter(date, maxDate);
                  const isSelected =
                    value &&
                    isSameDay(date, new Date(value));
                  return (
                    <button
                      key={idx}
                      disabled={disabled}
                      onClick={() => selectDay(date.getDate())}
                      className={clsx(
                        "text-sm p-1 rounded w-8 h-8",
                        disabled
                          ? "text-gray-300 cursor-not-allowed"
                          : isSelected
                          ? "bg-brand-400 text-white"
                          : "hover:bg-gray-100"
                      )}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>

      {error && <p className="text-xs text-red-500">{error.message}</p>}
    </div>
  );
}
